import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import { t } from "i18next";
import api from "../API/apiCollection";
import toast from "react-hot-toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
// Removed PayPal imports
// import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { useDispatch, useSelector } from "react-redux";
import { resetState } from "../redux/cart";
import { orderCartDetailsReset } from "../redux/orderCartDetails";
import { deliveryAddressReset } from "../redux/DeliveryAddress";
import { useNavigate } from "react-router";
import { resetStatePromo } from "../redux/Promocode";

// --- Midtrans Payment Component ---

// Note: You must include the Midtrans Snap script in your public/index.html or the main entry file:
// <script type="text/javascript" src="https://app.midtrans.com/snap/snap.js" data-client-key="YOUR_CLIENT_KEY"></script>
// Replace "YOUR_CLIENT_KEY" with the one from your settings, or load it dynamically.

// Midtrans global window object type definition (for reference, not part of the component)
/**
 * @typedef {object} MidtransSnap
 * @property {function(string, object): void} pay - Function to open the Snap payment window.
 */
// const snap = /** @type {MidtransSnap} */ (window.snap);

const Midtrans = ({ amount }) => {
  const settings = useSelector((state) => state?.Settings)?.settings
    ?.payment_gateways_settings;

  const orderDetails = useSelector(
    (state) => state.OrderCartDetails
  )?.orderDetails;
  const delivery_type = useSelector(
    (state) => state.DeliveryAddress
  )?.deliveryType;
  const deliveryAddress = useSelector(
    (state) => state.DeliveryAddress
  )?.delivery;

  const date = orderDetails && orderDetails.date;
  const time = orderDetails && orderDetails.slot;
  const address = deliveryAddress !== "" ? deliveryAddress : "";
  const orderNotes = orderDetails.orderNote;

  let promocode = useSelector((state) => state.Promocode);
  let selectedPromoCode = promocode.selectedPromoCode;

  const [order_id, setOrderID] = useState(0);
  const [loading, setLoading] = useState(false);
  
  // Dialog is no longer needed for Midtrans Snap as it opens its own pop-up/redirect
  // const [open, setOpen] = useState(false); 

  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Load Midtrans Snap.js script dynamically if not already present
  useEffect(() => {
    if (window.snap) return;

    const midtransClientKey = settings?.midtrans_client_key; // Assuming you have a client key in your settings
    if (!midtransClientKey) {
        console.error("Midtrans Client Key is missing in settings.");
        return;
    }

    const script = document.createElement('script');
    if (settings?.midtrans_mode == 'test') {
      script.src = "https://app.sandbox.midtrans.com/snap/snap.js";
    } else {
      script.src = "https://app.midtrans.com/snap/snap.js";
    }
    script.setAttribute('data-client-key', midtransClientKey);
    script.async = true;
    document.body.appendChild(script);

    return () => {
        document.body.removeChild(script);
    };
  }, [settings?.midtrans_client_key]); // Depend on the client key setting

  /**
   * Cleans up Redux state after a successful payment.
   */
  const handleSuccessCleanup = () => {
    dispatch(resetState());
    dispatch(orderCartDetailsReset());
    dispatch(deliveryAddressReset());
    dispatch(resetStatePromo());
  };

  /**
   * Handles actions after the payment is completed, regardless of status.
   * @param {string} status 'success', 'pending', or 'cancel'
   */
  const handlePaymentOutcome = async (oid, status) => {
    setLoading(false);
    
    // 1. Update the transaction status in your backend
    try {
        await api.add_transactions({ orderID: oid, status: status });
    } catch (error) {
        toast.error("Failed to update transaction status in the backend.");
        console.error("Error updating transaction:", error);
    }

    // 2. Perform cleanup for success and navigate
    if (status === "success") {
        handleSuccessCleanup();
    }
    
    // 3. Navigate to the order details page
    navigate("/profile/booking/services/" + oid);
  };
  

  /**
   * Calls the placeOrder API to create a transaction and get the Snap token.
   */
  const getSnapToken = async () => {
    if (loading) return;

    setLoading(true);
    
    try {
      // 1. Call your backend API to place the order and create the Midtrans transaction
      const result = await api.placeOrder({
        // IMPORTANT: Change method from 'paypal' to 'midtrans' or whatever your backend expects
        method: "midtrans", 
        date: date,
        time: time,
        addressId: delivery_type === "Home" ? address?.id : "",
        order_note: orderNotes,
        promo_code_id: selectedPromoCode ? selectedPromoCode.id : "",
        // Add currency if needed, Midtrans Snap token generation usually needs it
        // currency: 'IDR' 
      });

      if (result.error === false || result.error === "false") {
        const { order_id: newOrderId, midtrans_token: snapToken } = result.data;
        
        if (snapToken) {
            setOrderID(newOrderId);
            // 2. Open the Midtrans Snap payment window using the received token
            window.snap.pay(snapToken, {
                onSuccess: function(result){
                    // Payment successful callback
                    toast.success("Payment Successful!");
                    handlePaymentOutcome(newOrderId, "success");
                },
                onPending: function(result){
                    // Payment pending (e.g., bank transfer, virtual account)
                    toast.info("Payment Pending. Please complete the payment process.");
                    handlePaymentOutcome(newOrderId, "pending");
                },
                onError: function(result){
                    // Payment failed callback
                    toast.error("Payment Failed.");
                    handlePaymentOutcome(newOrderId, "failed");
                },
                onClose: function(){
                    // User closed the popup before finishing (not explicitly a failure)
                    toast.error("You closed the payment page before completion.");
                    handlePaymentOutcome(newOrderId, "cancel");
                }
            });
            // Setting loading to false is handled inside the snap.pay callbacks (handlePaymentOutcome)
        } else {
            toast.error("Midtrans Snap token is missing from the API response.");
            setLoading(false);
        }
      } else {
        // API returned an error
        if (typeof result.message === "object") {
          Object.values(result.message).forEach((e) => {
            toast.error(e);
          });
        } else {
          toast.error(result.message);
        }
        setLoading(false);
      }
    } catch (error) {
      console.error("Error placing order:", error);
      toast.error("Failed to place order. Please try again later.");
      setLoading(false);
    }
  };


  return (
    <Box>
      <Button
        fullWidth
        sx={{ my: 1 }}
        variant="outlined"
        onClick={getSnapToken}
        disabled={loading}
      >
        {loading ? t("loading") : `${t("make_a_payment")} ${t("now")}`}
      </Button>
      
      {/* The Dialog component and PayPalScriptProvider/PayPalButtons are removed
        as Midtrans Snap handles its own UI (pop-up/redirect).
      */}
    </Box>
  );
};

export default Midtrans; // Export the new component name